import { useCallback, useRef, useState, type ReactNode } from 'react'
import { Button, Modal } from '@indipia/ui-core'
import { periodSyncLines, periodSyncTitle, type PeriodSyncPlan } from '../constants/periodSync.logic'

// 공종 날짜 변경이 스케줄 시작·종료일을 넘을 때 묻는 확인창 (스케줄 공유 결정 4·5, T2 E-04·E-05·E-06).
// confirm(plan) 이 true 면 호출자는 요청에 syncSchedulePeriod: true 를 싣는다. 막지 않고 정보만 주며, 취소하면 저장하지 않는다.
// modal 은 호출하는 컴포넌트가 JSX 안에 한 번 렌더한다(useImmediateConfirm 과 같은 방식).

interface AskState { plan: PeriodSyncPlan; resolve: (ok: boolean) => void }

export function usePeriodSyncConfirm() {
  const [ask, setAsk] = useState<AskState | null>(null)
  const askRef = useRef<AskState | null>(null)
  askRef.current = ask

  const confirm = useCallback((plan: PeriodSyncPlan) => new Promise<boolean>(resolve => {
    setAsk({ plan, resolve })
  }), [])

  const close = (ok: boolean) => {
    const a = askRef.current
    setAsk(null)
    a?.resolve(ok)
  }

  const modal: ReactNode = (
    <Modal
      open={ask != null}
      onClose={() => close(false)}
      title={ask ? periodSyncTitle(ask.plan) : ''}
      footerAlign="stretch"
      footer={ask && (
        <>
          <Button variant="secondary" onClick={() => close(false)}>취소</Button>
          <Button variant="primary" onClick={() => close(true)}>진행</Button>
        </>
      )}
    >
      {ask && (
        <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--shell-on-surface)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {periodSyncLines(ask.plan).map(line => <div key={line}>{line}</div>)}
        </div>
      )}
    </Modal>
  )

  return { confirm, modal }
}

/** 확인 취소로 저장을 중단했을 때 던지는 오류 이름 — 호출자는 오류 토스트 없이 조용히 끝낸다. */
export const PERIOD_SYNC_CANCELLED = 'PERIOD_SYNC_CANCELLED'
export function periodSyncCancelledError(): Error {
  const e = new Error(PERIOD_SYNC_CANCELLED)
  e.name = PERIOD_SYNC_CANCELLED
  return e
}
